import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", access: true, is_admin: false });
  const [message, setMessage] = useState("");
  const [limitEdits, setLimitEdits] = useState({});
  const navigate = useNavigate();

  const getToken = useCallback(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user ? user.token : null;
  }, []);

  const checkLoginStatus = useCallback(() => {
    const token = getToken();
    if (!token) {
      navigate("/login");
    }
  }, [getToken, navigate]);

  const fetchUsers = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (response.ok) {
        setUsers(data);
      } else {
        console.error("Request failed with status:", response.status);
        setMessage("Error fetching users.");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage("Failed to fetch users.");
    }
  }, [getToken, navigate]);

  useEffect(() => {
    checkLoginStatus();
    fetchUsers();
  }, [checkLoginStatus, fetchUsers]);

  const handleLogout = async () => {
    localStorage.removeItem("user");
    localStorage.removeItem("subscriptions");

    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cache) => caches.delete(cache)));
    }

    navigate("/login");
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;

    // MODIFIED PART: Ensure newUser.is_admin is included in the payload
    const payload = {
      email: newUser.email,
      name: newUser.name,
      access: newUser.access,
      is_admin: newUser.is_admin, // <<< ADD THIS LINE
    };

    try {
      const response = await fetch(`${API_URL}/admin/add_user`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        // Reset form including the is_admin state
        setNewUser({ email: "", name: "", access: true, is_admin: false }); // <<< MODIFIED LINE
        fetchUsers(); // Refresh user list
      } else {
        setMessage(data.detail || "Error adding user.");
      }
    } catch (error) {
      console.error("Error adding user:", error);
      setMessage("Failed to add user. Check console for details.");
    }
  };

  const handleRemoveUser = async (email) => {
    if (!window.confirm(`Are you sure you want to remove ${email}?`)) {
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/admin/remove_user`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      setMessage(data.message);
      fetchUsers();
    } catch (error) {
      console.error("Error removing user:", error);
    }
  };

  const handleToggleAccess = async (email, access) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/admin/update_access`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, access: !access }),
      });
      const data = await response.json();
      setMessage(data.message);
      fetchUsers();
    } catch (error) {
      console.error("Error updating access:", error);
    }
  };

  const handleLimitChange = (email, newLimit) => {
    setLimitEdits((prev) => ({
      ...prev,
      [email]: newLimit,
    }));
  };

  const handleUpdateLimit = async (email) => {
    const token = getToken();
    if (!token) return;

    const seat_add_limit = limitEdits[email];
    if (!seat_add_limit) return;

    try {
      const response = await fetch(`${API_URL}/admin/update_seat_limit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          seat_add_limit: parseInt(seat_add_limit, 10),
        }),
      });

      const data = await response.json();
      setMessage(data.message || "Limit updated");
      fetchUsers();
    } catch (error) {
      console.error("Error updating seat limit:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto p-6 bg-white shadow-md rounded-lg">
        {message && (
          <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg border-l-4 border-green-500">
            {message}
          </div>
        )}
        <button
          onClick={handleLogout}
          className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg mb-6 shadow-md transition duration-300"
        >
          Logout
        </button>

        {/* Add New User */}
        <div className="bg-gray-100 p-6 rounded-lg mb-6 shadow-sm">
          <h2 className="text-xl -semiboldfont mb-4 text-gray-700">
            Add New User
          </h2>
          <form onSubmit={handleAddUser} className="flex space-x-4">
            <input
              type="email"
              value={newUser.email}
              onChange={(e) =>
                setNewUser({ ...newUser, email: e.target.value })
              }
              placeholder="Email"
              required
              className="border p-3 rounded-lg w-full"
            />
            <input
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              placeholder="Name"
              required
              className="border p-3 rounded-lg w-full"
            />
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is-admin"
                checked={newUser.is_admin}
                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="is-admin" className="text-gray-700">Admin</label>
            </div>
            <button
              type="submit"
              className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg"
            >
              Add User
            </button>
          </form>
        </div>

        {/* Manage Users */}
        <div className="bg-white shadow-md rounded-lg p-4">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            Manage Users
          </h2>
          <table className="min-w-full bg-white border rounded-lg">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-3 px-4 text-left text-gray-600 font-medium">
                  User
                </th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">
                  Email
                </th>
                <th className="py-3 px-4 text-center text-gray-600 font-medium">
                  Access
                </th>
                <th className="py-3 px-4 text-center text-gray-600 font-medium">
                  Admin
                </th>
                <th className="py-3 px-4 text-center text-gray-600 font-medium">
                  Seat Add Limit
                </th>
                <th className="py-3 px-4 text-center text-gray-600 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.email}
                  className="border-t hover:bg-gray-50 transition-colors"
                >
                  <td className="py-3 px-4 text-gray-800">{user.name}</td>
                  <td className="py-3 px-4 text-gray-800">{user.email}</td>
                  <td className="py-3 px-4 text-center">
                    <span
                      className={
                        user.access
                          ? "text-green-600 font-medium"
                          : "text-red-600 font-medium"
                      }
                    >
                      {user.access ? "Granted" : "Revoked"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span
                      className={
                        user.is_admin
                          ? "text-blue-600 font-medium"
                          : "text-gray-600 font-medium"
                      }
                    >
                      {user.is_admin ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <input
                      type="number"
                      className="border p-1 w-16"
                      defaultValue={user.seat_add_limit || 10}
                      onChange={(e) =>
                        handleLimitChange(user.email, e.target.value)
                      }
                    />
                  </td>
                  <td className="py-3 px-4 text-center space-x-2">
                    <button
                      onClick={() => handleUpdateLimit(user.email)}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg"
                    >
                      Update Limit
                    </button>
                    <button
                      onClick={() =>
                        handleToggleAccess(user.email, user.access)
                      }
                      className="bg-yellow-400 hover:bg-yellow-500 text-white py-2 px-4 rounded-lg"
                    >
                      {user.access ? "Revoke" : "Grant"} Access
                    </button>
                    <button
                      onClick={() => handleRemoveUser(user.email)}
                      className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
